import React from "react";

import statsPlot from "./assets/stats.svg";
import correlationPlot from "./assets/correlation.svg";

function CovidLethalityMakingOf() {
  return (
    <>
      <p>
        De nombreux phénomènes peuvent expliquer la différence de mortalité
        entre mars 2019 et mars 2020. Le Covid-19 n’est que l’un d’entre eux.
        Dans certains département, son impact est trop faible pour être
        décelable. Nous avons donc limité{" "}
        <a href="https://lemediatv.fr/articles/analyses/data-les-morts-invisibles-du-coronavirus-la-verite-derriere-les-chiffres-officiels-SaYKcOT9RFaTkUMNO7HOYg">
          notre article à l’étude des départements pour lesquels l’impact est
          particulièrement significatif
        </a>
        .
      </p>
      <p>
        Pour tester l’hypothèse selon laquelle la hausse de mortalité que nous
        constations dans ces départements était bien attribuable en grande
        partie au Covid-19, nous avons réalisés plusieurs tests.
      </p>
      <p>
        La première vérification que nous avons faite a été de s’assurer que la
        mortalité était stable entre 2019 et 2020 sur le mois de mars dans les
        départements épargnés par le Covid-19. Pour cela, nous avons estimé la
        différence de mortalité entre 2019 et 2020 dans 10 départements
        officiellement sans décès lié au Covid-19. Si notre hypothèse est
        exacte, nous devrions trouver une différence de mortalité proche de zéro
        dans chacun de ces départements. Nous ne nous attendons pas à trouver
        exactement zéro, car la mortalité peut-être considérée comme une
        variable aléatoire, déterminée par un processus stochastique. Pour cette
        raison, nous avons recours à ce qu’on appelle des “intervalles de
        confiance”. Ces intervalles décrivent l’ampleur des fluctuations
        statistiques attendues. Voici le résultat :
      </p>

      <figure>
        <img
          src={statsPlot}
          alt="Graphique illustrant la variation de la mortalité entre 2019 et 2020"
        />
        <figcaption>
          <strong>
            Variation de la mortalité entre 2019 et 2020 pour la période du 1er
            au 30 mars pour 10 départements épargnés par le Covid-19
          </strong>
          .<br />
          Les barres verticales représentent l'intervalle de confiance à 95 %.
        </figcaption>
      </figure>

      <p>
        Les variations de mortalité estimées sont représentées par les points,
        département par département. Les intervalles de confiance sont
        représentés par les barres verticales. Si la barre inclut la valeur 0,
        on dit que la mortalité est “compatible” avec une variation nulle. C’est
        le cas de tous les départements, sauf le 46 et le 24. Dans ces
        départements, on peut affirmer que la mortalité a baissé de manière
        significative en 2020. Par ailleurs, dans tous les départements, on peut
        affirmer que l'espérance de mortalité (le nombre moyen de décès
        attendus), n’a certainement pas augmenté de plus de 50 décès par mois,
        ou tout au plus de quelques dizaines de décès par mois.
      </p>
      <p>
        La conclusion générale est la suivante : la mortalité observée en 2020,
        dans les départements non touchés par le Covid-19, est en général
        compatible avec celle observée en 2019. Les seuls écarts significatifs
        indiquent une baisse de la mortalité dans ces départements en 2020. De
        plus, une augmentation de la mortalité au-delà de quelques dizaines de
        décès hors Covid-19 est exclue par ces observations.
      </p>
      <p>
        Une fois rassurés sur ce point, nous pouvons nous intéresser plus
        globalement à la corrélation entre le nombre de décès attribués au
        Covid-19 et la variation de mortalité constatée. Pour cela on place
        chaque département sur un même graphique, avec un intervalle de
        confiance à titre indicatif qui indique grossièrement l’ampleur de
        fluctuations attendues. Voici ce qu’on obtient :
      </p>

      <figure>
        <img
          src={correlationPlot}
          alt="Graphique illustrant la corrélation entre le nombre de décès officiellement attribués au Covid-19 et la surmortalité estimée en mars 2020"
        />
        <figcaption>
          <strong>
            Corrélation entre le nombre de décès officiellement attribués au
            Covid-19 et la surmortalité estimée en mars 2020,
            <br /> pour chaque département de France.
          </strong>
        </figcaption>
      </figure>

      <p>
        On observe qualitativement que les départements peu touchés connaissent
        des variations faibles de mortalité, en général à tendance négative, ce
        qui confirme notre analyse précédente. On observe que la surmortalité
        est bien plus élevée dans les départements touchés. La tendance est
        grossièrement décrite par une droite de pente 1,8. Comme cette droite
        semble grossièrement décrire la relation entre surmortalité et nombre
        officiel de victimes du Covid-19, il semble bien que le Covid-19 en soit
        la cause. Le fait que cette pente soit plus grande que 1 suggère le
        nombre de décès dus au Covid-19 est bien sous-évalué.
      </p>
      <p>
        Cette méthode est bien sûre très limitée. Les analyses qui visent à
        estimer chaque année la mortalité imputable à la grippe, par exemple,
        sont plus sophistiquées. Elles consistent, grossièrement, dans un
        premier temps, à calculer la surmortalité observée par rapport à la
        mortalité attendue hors épidémies. Cette mortalité attendue peut-être
        obtenue à partir de modèles approximatifs qui tiennent comptent de la
        variation démographique sur des temps longs, ainsi que des variations
        saisonnières. En corrélant cette surmortalité avec des données
        sanitaires issues d’un échantillon de médecins, comme le réseau
        sentinelles en France, il est possible d’imputer une partie de cet excès
        de surmortalité à la grippe. De telles analyses, pour le Covid-19,
        permettront de mieux quantifier la surmortalité imputable à cette
        épidémie.
      </p>
      <p>
        Malgré tout, nos constats sont déjà confortés par les données qui
        s’accumulent. Nos observations suggèrent que le décompte officiel pour
        mars ne reflétait environ que la moitié de la surmortalité. Or ces
        données ne comprenaient pas les décès survenus en EHPAD. Ceux-ci ne sont
        publiés que depuis quelques jours. À cette heure,{" "}
        <a
          href="https://www.nouvelobs.com/coronavirus-de-wuhan/20200406.OBS27159/8-911-deces-en-france-depuis-le-debut-de-l-epidemie-de-covid-19-a-l-hopital-et-en-ehpad.html"
          target="_blank noopener noreferrer"
        >
          aux 6.500 décès survenus à l’hôpital s’ajoutent désormais
          officiellement 2.400 décès en EHPAD
        </a>{" "}
        : une partie significative des décès était en effet invisible dans les
        données de mars.
      </p>
    </>
  );
}

export default CovidLethalityMakingOf;
